/**
 * One record on the meeting page's Legislation tab (design: Meeting v2 — Legislation tab vpHXI, rows "Bill <record>"
 * under w5T5w5): chips (type, Key legislation, record number), the title, the latest action line (date · action · body ·
 * Sponsor names), the 5-step timeline and the follow bell. The whole row opens the record's legislation page.
 */
import { Bell, BellRing, Play } from "lucide-react";
import { findFollow, followsStore, useFollows } from "../../stores/followsStore";
import { ProgressTracker } from "../shared/ProgressTracker";
import { ListRow, RowActions, RowCell, RowMain, RowMeta, RowTitle, RowTrail } from "../shared/List";
import { clock } from "../../lib/format";
import { actionLabel, bodyLabel, readingName } from "../../legislation/stages";
import type { Legislation, Person } from "../../types";
import { getViewer, legislationHref, personHref } from "../../app/viewer";
import { site } from "../../app/site";

const POSITIVE_ACTION = /recommended to pass|passed|adopted|approved/i;
const MAX_SPONSORS = 2;   // lead + one, as the design's longest line ("Michelle A. Harris, Emma Mitts"); the rest in the tooltip
const day = (iso: string) => new Date(iso.length === 10 ? `${iso}T12:00:00Z` : iso).toLocaleDateString("en-US", { timeZone: site.timeZone, month: "short", day: "numeric" });
const TYPE: Record<string, string> = { ordinance: "Ordinance", resolution: "Resolution", order: "Order", appointment: "Appointment", claim: "Claim", report: "Report", communication: "Communication" };
const typeLabel = (t: string) => TYPE[t.toLowerCase()] ?? (t ? t[0].toUpperCase() + t.slice(1) : "");

/** Sponsors as the row names them, lead first: alderpersons from the meeting's people, others from the Clerk's name. */
export function sponsorsOf(bill: Legislation, people: Record<string, Person>) {
  return bill.sponsors.map((s) => ({ id: s, name: people[s]?.name ?? readingName(s), href: /^a:\d+$/.test(s) ? personHref(s) : getViewer().publicPage ? undefined : `/dashboard/people?q=${encodeURIComponent(readingName(s))}` }));
}

export function BillRow({ bill, people, onJump }: { bill: Legislation; people: Record<string, Person>; onJump?: (at: number) => void }) {
  const followed = useFollows((s) => findFollow(s.follows, "legislation", bill.id));
  const action = actionLabel(bill.latestAction.action || "");
  const body = bodyLabel(bill.latestAction.body || "");
  const sponsors = sponsorsOf(bill, people);
  const shown = sponsors.slice(0, MAX_SPONSORS);
  return (
    <ListRow className="bill-row" href={legislationHref(bill.id)}>
      <RowMain min={200} className="bill-text">
        <RowMeta className="bill-meta">
          {bill.type && <span className="chip chip--outline">{typeLabel(bill.type)}</span>}
          {bill.isKey && <span className="chip chip--warning">Key legislation</span>}
          <span className="bill-id">{bill.id}</span>
        </RowMeta>
        <RowTitle as="h4" lines={3} className="bill-title" title={bill.title}>{bill.title}</RowTitle>
        {(bill.latestAction.date || action) && (
          <p className="bill-action">
            {bill.latestAction.date && <span className="muted">{day(bill.latestAction.date)}</span>}
            {action && <span className="bill-action-name" data-positive={POSITIVE_ACTION.test(action) || undefined}>{action}</span>}
            {body && <span className="muted">· {body}</span>}
            {shown.length > 0 && (
              <>
                <span className="muted">·</span><span className="muted">Sponsor</span>
                <span className="bill-sponsor" title={sponsors.length > shown.length ? sponsors.map((s) => s.name).join(", ") : undefined}>
                  {shown.map((s, i) => <span key={s.id}>{i > 0 && ", "}{s.href ? <a href={s.href}>{s.name}</a> : s.name}</span>)}
                </span>
              </>
            )}
          </p>
        )}
      </RowMain>
      {bill.at != null && onJump && <RowTrail><button className="jump" onClick={() => onJump(bill.at!)}><Play size={10} />{clock(bill.at)}</button></RowTrail>}
      <RowCell className="bill-tracker"><ProgressTracker stage={bill.stage} /></RowCell>
      <RowActions>
        <button
          className="bell-btn"
          aria-pressed={!!followed}
          aria-label={followed ? `Stop following ${bill.id}` : `Follow ${bill.id}`}
          onClick={() => (followed ? followsStore.getState().unfollow(followed.id) : followsStore.getState().follow({ type: "legislation", refId: bill.id, label: bill.title }))}
        >
          {followed ? <BellRing size={14} /> : <Bell size={14} />}
        </button>
      </RowActions>
    </ListRow>
  );
}
